import { DisplayTime } from '../utils/displayTime'

// Fallback chain prevents the "405 on /api/auth/login" class of bug when a
// Vercel preview build ships without VITE_API_URL: relative calls would hit
// vercel.json's index.html rewrite, which can't handle POST.
// - In dev: stay relative so the Vite proxy to localhost:3001 works.
// - In prod: fall back to api.99medias.com (Cloudflare-proxied Railway origin).
const API_URL =
  import.meta.env.VITE_API_URL ||
  (import.meta.env.PROD ? 'https://api.99medias.com' : '')

class ApiService {
  constructor() {
    this.accessToken = localStorage.getItem('access_token')
    this.lastUnauthorizedTime = null
    this.unauthorizedCount = 0
    this.lastSuccessTime = null
  }

  setToken(token) {
    this.accessToken = token
    if (token) {
      localStorage.setItem('access_token', token)
    } else {
      localStorage.removeItem('access_token')
    }
  }

  getToken() {
    return this.accessToken || localStorage.getItem('access_token')
  }

  async request(endpoint, options = {}) {
    const url = `${API_URL}/api${endpoint}`

    // Generate traceId for request tracing
    const traceId = options.traceId || crypto.randomUUID()

    const headers = {
      'Content-Type': 'application/json',
      'X-Trace-ID': traceId,
      ...options.headers,
    }

    const token = this.getToken()
    if (token) {
      headers.Authorization = `Bearer ${token}`
    }

    // Log request with traceId
    console.log(`[${traceId}] ${options.method || 'GET'} ${endpoint}`)

    // Retry logic for Railway cold starts and transient errors
    // IMPORTANT: Disable retries for financial mutations to prevent duplicates
    const isFinancialMutation = (options.method === 'POST' || options.method === 'PUT') &&
      (endpoint.includes('/ledger-entry') || endpoint.includes('/initial-balance') || endpoint.includes('/adjust-balance'))
    // Callers can override retries for expensive/idempotent-once endpoints (e.g. the
    // bulk-TSV generate, which re-runs a heavy live-IO assemble on every attempt —
    // retrying just hammers an already-struggling extension and triples server load).
    const maxRetries = Number.isInteger(options.maxRetries)
      ? Math.max(1, options.maxRetries)
      : (isFinancialMutation ? 1 : 3)
    let lastError = null

    // Per-attempt timeout. Browser fetch has no default timeout — if the
    // response packet drops between Cloudflare edge and the client (stuck
    // keepalive socket, edge pool churn) the promise hangs *forever*.
    // The May 7 2026 login outage manifested this way: backend logged a
    // 200 in 177ms, user's button stayed on "One moment" indefinitely
    // because fetch never resolved or rejected. AbortController with a
    // generous timeout converts the hang into a retryable error so the
    // outer retry loop + the catch-block in the caller can recover.
    // 30s is well above legitimate p99 (~3s) but well below "the user
    // gives up and reloads" (typically 10-15s).
    const timeoutMs = options.timeoutMs || 30_000

    for (let attempt = 1; attempt <= maxRetries; attempt++) {
      const controller = new AbortController()
      const timeoutId = setTimeout(() => controller.abort(), timeoutMs)
      try {
        const response = await fetch(url, {
          ...options,
          headers,
          signal: controller.signal,
        })
        clearTimeout(timeoutId)

        // Success or client error (4xx) - don't retry
        if (response.ok || (response.status >= 400 && response.status < 500)) {
          return await this.handleResponse(response, traceId, endpoint, options)
        }

        // Server error (5xx) - retry
        if (response.status >= 500 && attempt < maxRetries) {
          const delay = Math.pow(2, attempt - 1) * 1000 // 1s, 2s, 4s
          console.warn(`[${traceId}] ${response.status} error, retrying in ${delay}ms (attempt ${attempt}/${maxRetries})`)
          await new Promise(resolve => setTimeout(resolve, delay))
          continue
        }

        // Last attempt with 5xx - handle response
        return await this.handleResponse(response, traceId, endpoint, options)

      } catch (error) {
        clearTimeout(timeoutId)

        // AbortError from our timeout: rewrite to a friendlier message so
        // the caller's catch block can show a useful toast. Treated as a
        // network error for retry purposes.
        if (error.name === 'AbortError') {
          error = new Error(`Request timed out after ${timeoutMs}ms`)
        }

        // Don't retry configuration errors (NO_TOKEN, Unauthorized, 404 Not Found)
        // These are not transient network issues
        const isConfigError = error.message?.startsWith('NO_TOKEN') ||
                              error.message === 'Unauthorized'
        const isNotFoundError = error.message?.includes('404') ||
                                error.message?.includes('Not Found')
        // Don't retry rate limit errors (429) - retrying immediately won't help
        const status = error?.status ?? error?.response?.status
        const isRateLimitError = status === 429 ||
                                 error.message?.includes('Too many requests') ||
                                 error.message?.includes('rate limit') ||
                                 error.message?.includes('rate-limited') ||
                                 error.message?.includes('429')
        if (isConfigError || isNotFoundError || isRateLimitError) {
          throw error
        }

        // Network error or CORS error - retry
        lastError = error
        if (attempt < maxRetries) {
          const delay = Math.pow(2, attempt - 1) * 1000 // 1s, 2s, 4s
          console.warn(`[${traceId}] Network error, retrying in ${delay}ms (attempt ${attempt}/${maxRetries}):`, error.message)
          await new Promise(resolve => setTimeout(resolve, delay))
          continue
        }
      }
    }

    // All retries exhausted
    console.error(`[${traceId}] All ${maxRetries} attempts failed`)
    throw lastError || new Error('Request failed after retries')
  }

  // Build an Error that preserves the HTTP status + parsed body so callers
  // can branch on status without parsing the message string. Axios-shaped
  // `response: { status, data }` is included for compatibility with code
  // written against axios; `error.status` is the convenience accessor.
  _buildApiError(message, status, data) {
    const err = new Error(message)
    err.status = status
    err.response = { status, data: data ?? null }
    return err
  }

  // Restored 2026-04-20 after commit 7afa2f86 ("purge 139 dead methods")
  // accidentally deleted it — `request()` above still calls it on every
  // hop, so absence broke every API call in production.
  async handleResponse(response, traceId, endpoint, options = {}) {
    if (response.status === 401) {
      let errorData = null
      try {
        const clone = response.clone()
        errorData = await clone.json()
      } catch (_) { /* non-JSON body, fall through */ }

      // NO_TOKEN means the profile needs API token configuration — not an
      // auth failure. Don't trip the logout counter; user stays signed in.
      if (errorData?.code === 'NO_TOKEN') {
        console.log('[API] NO_TOKEN - profile requires API token configuration')
        throw this._buildApiError('NO_TOKEN: Profile requires API token', 401, errorData)
      }

      // JWT refresh hop (F-D4.a) — try a single refresh before tripping the
      // cascade counter. Feature-flagged because this is the global API path.
      if (!options._isRetry && import.meta.env.VITE_JWT_REFRESH_ENABLED === 'true') {
        const isExpiryResponse = errorData?.error && /Invalid or expired token/i.test(errorData.error)
        if (isExpiryResponse) {
          try {
            const { supabase } = await import('../lib/supabase')
            const { data, error } = await supabase.auth.refreshSession()
            if (!error && data?.session?.access_token) {
              console.log('[API] JWT refreshed silently')
              this.setToken(data.session.access_token)
              try {
                const { setSupabaseSession } = await import('../lib/supabase')
                await setSupabaseSession(data.session)
              } catch (sessionErr) {
                console.warn('[API] Supabase session wiring after refresh failed:', sessionErr)
              }
              return this.request(endpoint, { ...options, traceId, _isRetry: true })
            }
            console.warn('[API] JWT refresh returned no session; falling through to cascade counter', error)
          } catch (refreshErr) {
            console.warn('[API] JWT refresh threw; falling through to cascade counter', refreshErr)
          }
        }
      }

      // Brief grace window: if we just had a successful authed request,
      // a stray 401 is almost certainly a Railway redeploy blip.
      const now = Date.now()
      if (this.lastSuccessTime && (now - this.lastSuccessTime) < 30000) {
        console.warn('[API] 401 ignored - had successful request within last 30s (likely deploy)')
        throw this._buildApiError('Unauthorized', 401, errorData)
      }

      // The dashboard fans out 10+ parallel queries. During Railway
      // redeploys you can hit several 401s in a row from one bad window —
      // a low threshold logs the user out for what is really a 2-minute
      // server hiccup. 8 failures within a 2-min window is the "real" bar.
      const GRACE_PERIOD_MS = 120000
      const MAX_FAILURES = 8

      if (!this.lastUnauthorizedTime || (now - this.lastUnauthorizedTime) > GRACE_PERIOD_MS) {
        this.unauthorizedCount = 1
        this.lastUnauthorizedTime = now
        console.warn(`[API] 401 Unauthorized - attempt 1 of ${MAX_FAILURES} before logout`)
        throw this._buildApiError('Unauthorized', 401, errorData)
      }

      this.unauthorizedCount++
      this.lastUnauthorizedTime = now

      if (this.unauthorizedCount >= MAX_FAILURES) {
        console.error('[API] Multiple 401 errors - logging out')
        this.setToken(null)
        localStorage.removeItem('auth-storage')
        window.location.href = '/login'
      } else {
        console.warn(`[API] 401 Unauthorized - attempt ${this.unauthorizedCount} of ${MAX_FAILURES} before logout`)
      }

      throw this._buildApiError('Unauthorized', 401, errorData)
    }

    // Success path: reset 401 tracker.
    this.unauthorizedCount = 0
    this.lastUnauthorizedTime = null
    this.lastSuccessTime = Date.now()

    // Parse body, tolerating non-JSON (502 HTML, plain text, etc.)
    let data
    try {
      const contentType = response.headers.get('content-type')
      if (contentType && contentType.includes('application/json')) {
        data = await response.json()
      } else {
        const text = await response.text()
        data = { error: `Server error: ${response.status} ${response.statusText}`, details: text.substring(0, 200) }
      }
    } catch (parseError) {
      console.error(`[${traceId}] Failed to parse response:`, parseError.message)
      data = { error: `Invalid response: ${response.status} ${response.statusText}` }
    }

    const responseTraceId = response.headers.get('X-Trace-ID') || traceId
    console.log(`[${responseTraceId}] Response: ${response.status}`)

    if (!response.ok) {
      const errorMessage = data.error || data.message || data.details || data.code || 'Request failed'
      console.error(`[${responseTraceId}] Error:`, errorMessage)
      throw this._buildApiError(errorMessage, response.status, data)
    }

    // Axios-compatible shape: callers can use either `response.data.x`
    // (modern) or `response.x` (legacy spread). Both supported.
    const result = { data }
    if (typeof data === 'object' && data !== null && !Array.isArray(data)) {
      Object.assign(result, data)
    }
    return result
  }

  // HTTP method helpers
  async get(endpoint, options = {}) {
    // Handle axios-style params option: api.get('/url', { params: { foo: 'bar' } })
    let url = endpoint
    if (options.params) {
      const searchParams = new URLSearchParams()
      for (const [key, value] of Object.entries(options.params)) {
        if (value !== undefined && value !== null) {
          searchParams.append(key, String(value))
        }
      }
      const queryString = searchParams.toString()
      if (queryString) {
        url += (url.includes('?') ? '&' : '?') + queryString
      }
    }
    return this.request(url, { ...options, method: 'GET' })
  }

  async post(endpoint, body, options = {}) {
    return this.request(endpoint, {
      ...options,
      method: 'POST',
      body: body ? JSON.stringify(body) : undefined,
    })
  }

  async put(endpoint, body, options = {}) {
    return this.request(endpoint, {
      ...options,
      method: 'PUT',
      body: body ? JSON.stringify(body) : undefined,
    })
  }

  async patch(endpoint, body, options = {}) {
    return this.request(endpoint, {
      ...options,
      method: 'PATCH',
      body: body ? JSON.stringify(body) : undefined,
    })
  }

  async delete(endpoint, options = {}) {
    return this.request(endpoint, { ...options, method: 'DELETE' })
  }

  // Auth
  async login(email, password) {
    // Reset 401 counter before login to prevent stale counter from previous session
    this.unauthorizedCount = 0
    this.lastUnauthorizedTime = null
    const response = await this.request('/auth/login', {
      method: 'POST',
      body: JSON.stringify({ email, password }),
    })
    // Access via .data for axios-style or directly for backward compat
    const session = response.data?.session || response.session
    this.setToken(session?.access_token)
    if (session?.access_token) {
      try {
        const { setSupabaseSession } = await import('../lib/supabase')
        await setSupabaseSession(session)
      } catch (sessionErr) {
        console.warn('[API] Supabase session wiring after login failed:', sessionErr)
      }
    }
    return response
  }

  async logout() {
    await this.request('/auth/logout', { method: 'POST' })
    this.setToken(null)
  }

  async getMe() {
    return this.request('/auth/me')
  }

  // Password Management
  async changePassword(currentPassword, newPassword) {
    return this.request('/auth/change-password', {
      method: 'POST',
      body: JSON.stringify({ currentPassword, newPassword })
    })
  }

  // Two-Factor Authentication (MFA)
  async mfaEnroll() {
    return this.request('/auth/mfa/enroll', {
      method: 'POST'
    })
  }

  async mfaVerify(code) {
    return this.request('/auth/mfa/verify', {
      method: 'POST',
      body: JSON.stringify({ code })
    })
  }

  async mfaDisable(password) {
    return this.request('/auth/mfa/disable', {
      method: 'POST',
      body: JSON.stringify({ password })
    })
  }

  async mfaChallenge(userId, code, useBackupCode = false) {
    return this.request('/auth/mfa/challenge', {
      method: 'POST',
      body: JSON.stringify({ userId, code, useBackupCode })
    })
  }

  async getMfaStatus() {
    return this.request('/auth/mfa/status')
  }

  // Email Management
  async changeEmail(newEmail, password) {
    return this.request('/auth/change-email', {
      method: 'POST',
      body: JSON.stringify({ newEmail, password })
    })
  }

  // Analytics
  async getRecentActivity() {
    return this.request('/admin/analytics/activity')
  }

  async getLiveCapacity() {
    return this.request('/admin/analytics/live-capacity')
  }

  // Live Calls - Real-time tracking
  async getLiveCalls() {
    return this.request('/calls/live')
  }

  async getCapacityStats() {
    return this.request('/insights/capacity-stats')
  }

  async getLostCalls(period = 'today') {
    return this.request(`/admin/analytics/lost-calls?period=${period}`)
  }

  // Today's Dashboard - all data in one call
  async getTodayDashboard(date = null, dateRange = null) {
    const params = new URLSearchParams()
    if (dateRange?.startDate && dateRange?.endDate) {
      params.set('startDate', dateRange.startDate)
      params.set('endDate', dateRange.endDate)
    } else if (date) {
      params.set('date', date)
    }
    const queryString = params.toString()
    return this.request(`/admin/dashboard/today${queryString ? '?' + queryString : ''}`)
  }

  // Profit summary - revenue vs ad spend
  async getProfitSummary(date = null, dateRange = null) {
    const params = new URLSearchParams()
    if (dateRange) {
      params.set('startDate', dateRange.startDate)
      params.set('endDate', dateRange.endDate)
    } else if (date) {
      params.set('date', date)
    }
    const queryString = params.toString()
    return this.request(`/admin/dashboard/profit-summary${queryString ? '?' + queryString : ''}`)
  }

  // Get total Adpos card spend for date/range with self-healing diagnostics
  async getAdposCardSpend(date = null, dateRange = null) {
    const params = new URLSearchParams()
    if (dateRange) {
      params.set('startDate', dateRange.startDate)
      params.set('endDate', dateRange.endDate)
    } else if (date) {
      params.set('date', date)
    }
    const query = params.toString() ? `?${params.toString()}` : ''
    // Use smart endpoint that provides diagnostics when spend is $0
    return this.request(`/admin/billing/total-spend-smart${query}`)
  }

  // Sync all ad accounts spend data
  async syncAdAccounts(datePreset = 'today') {
    return this.request('/admin/ad-accounts/sync', {
      method: 'POST',
      body: JSON.stringify({ date_preset: datePreset }),
    })
  }

  _buildSpendByGeoParams(dateOrRange = null) {
    const params = new URLSearchParams()
    if (dateOrRange?.startDate && dateOrRange?.endDate) {
      params.set('startDate', dateOrRange.startDate)
      params.set('endDate', dateOrRange.endDate)
      if (dateOrRange.preset) params.set('preset', dateOrRange.preset)
      return params
    }

    if (typeof dateOrRange !== 'string' || !dateOrRange) return params

    const today = DisplayTime.nowParisDateStr()
    const daysAgo = (days) => DisplayTime.shiftParisDate(today, -days)
    const setRange = (startDate, endDate = today, preset = null) => {
      params.set('startDate', startDate)
      params.set('endDate', endDate)
      if (preset) params.set('preset', preset)
    }

    switch (dateOrRange) {
      case 'today':
        return params
      case 'yesterday':
        params.set('date', daysAgo(1))
        return params
      case 'last_7d':
        setRange(daysAgo(6), today, dateOrRange)
        return params
      case 'last_30d':
        setRange(daysAgo(29), today, dateOrRange)
        return params
      case 'this_month':
        setRange(`${today.slice(0, 8)}01`, today, dateOrRange)
        return params
      default:
        if (/^\d{4}-\d{2}-\d{2}$/.test(dateOrRange)) {
          params.set('date', dateOrRange)
        }
        return params
    }
  }

  // Spend by geo (FR, BE, UK breakdown)
  async getSpendByGeo(dateOrRange = null) {
    const params = this._buildSpendByGeoParams(dateOrRange)
    const query = params.toString()
    return this.request(`/admin/dashboard/spend-by-geo${query ? '?' + query : ''}`)
  }

  // Weekday stats for comparison widget
  async getWeekdayStats(range = 'last_4_weeks') {
    return this.request(`/admin/dashboard/weekday-stats?range=${range}`)
  }

  // Ad account statistics for dashboard
  // Top performers for dashboard (buyers, destinations, peak hours)
  async getTopPerformers(date = null, dateRange = null) {
    const params = new URLSearchParams()
    if (dateRange?.startDate && dateRange?.endDate) {
      params.set('startDate', dateRange.startDate)
      params.set('endDate', dateRange.endDate)
    } else if (date) {
      params.set('date', date)
    }
    const queryString = params.toString()
    return this.request(`/admin/dashboard/top-performers${queryString ? '?' + queryString : ''}`)
  }

  // Balance owed by buyers (for dashboard)
  async getBalanceOwed() {
    return this.request('/admin/dashboard/balance-owed')
  }

  // AdPos billing cards with balances
  // Options: { activeOnly: true, withSpend: true, live: true, checkFailedTx: true }
  // live=true fetches directly from Adpos API for accurate balances (slower)
  // checkFailedTx=true checks for recent failed/declined transactions (slower)
  async getBillingCards(options = {}) {
    const params = new URLSearchParams()
    if (options.activeOnly) params.set('activeOnly', 'true')
    if (options.withSpend) params.set('withSpend', 'true')
    if (options.live) params.set('live', 'true')
    if (options.checkFailedTx) params.set('checkFailedTx', 'true')
    const query = params.toString()
    return this.request(`/admin/billing/cards${query ? `?${query}` : ''}`)
  }

  // Period comparison (current vs previous period)
  // Dialics status - live calls, completed today, balance
  // Orphan calls - converted calls missing buyer assignment
  // Retry orphan calls to re-fetch buyer info from Dialics
  // Admin - Sync
  async triggerSync(options = {}) {
    return this.request('/admin/sync/dialics', {
      method: 'POST',
      body: JSON.stringify(options),
    })
  }

  // Admin - Mappings
  async getCampaigns() {
    return this.request('/admin/mappings/campaigns')
  }

  // Insights / Smart Dashboard
  async getAdminInsights(dateRange) {
    const params = this._buildDateRangeParams(dateRange)
    return this.request(`/insights/admin?${params}`)
  }

  // Insights drilldown - get calls matching specific criteria
  async getInsightsDrilldown({ filterType, filterValue, dateRange, sortField, sortDir, page, limit }) {
    const params = new URLSearchParams()
    params.append('filterType', filterType)
    if (filterValue !== undefined && filterValue !== null) {
      params.append('filterValue', filterValue)
    }
    // Always send a range parameter - default to 'today' if not provided
    if (dateRange) {
      if (dateRange.preset === 'custom' && dateRange.start && dateRange.end) {
        params.append('range', 'custom')
        params.append('startDate', dateRange.start)
        params.append('endDate', dateRange.end)
      } else {
        params.append('range', dateRange.preset || 'today')
      }
    } else {
      // No dateRange provided, default to today
      params.append('range', 'today')
    }
    if (sortField) params.append('sortField', sortField)
    if (sortDir) params.append('sortDir', sortDir)
    if (page) params.append('page', page)
    if (limit) params.append('limit', limit)
    return this.request(`/insights/drilldown?${params}`)
  }

  // ==========================================
  // BINOM / CLICKS API
  // ==========================================

  async getClicksSummary(params = {}) {
    const searchParams = new URLSearchParams()
    if (params.range) searchParams.append('range', params.range)
    if (params.startDate) searchParams.append('startDate', params.startDate)
    if (params.endDate) searchParams.append('endDate', params.endDate)
    return this.request(`/admin/clicks/summary?${searchParams}`)
  }

  async getClickToCall(params = {}) {
    const searchParams = new URLSearchParams()
    if (params.range) searchParams.append('range', params.range)
    if (params.startDate) searchParams.append('startDate', params.startDate)
    if (params.endDate) searchParams.append('endDate', params.endDate)
    return this.request(`/admin/clicks/click-to-call?${searchParams}`)
  }

  async getClicksQuality(params = {}) {
    const searchParams = new URLSearchParams()
    if (params.range) searchParams.append('range', params.range)
    if (params.startDate) searchParams.append('startDate', params.startDate)
    if (params.endDate) searchParams.append('endDate', params.endDate)
    return this.request(`/admin/clicks/quality?${searchParams}`)
  }

  async getClicksFunnel(params = {}) {
    const searchParams = new URLSearchParams()
    if (params.range) searchParams.append('range', params.range)
    if (params.startDate) searchParams.append('startDate', params.startDate)
    if (params.endDate) searchParams.append('endDate', params.endDate)
    return this.request(`/admin/clicks/funnel?${searchParams}`)
  }

  async getClicksLog(params = {}) {
    const searchParams = new URLSearchParams()
    if (params.range) searchParams.append('range', params.range)
    if (params.startDate) searchParams.append('startDate', params.startDate)
    if (params.endDate) searchParams.append('endDate', params.endDate)
    if (params.limit) searchParams.append('limit', params.limit)
    return this.request(`/admin/clicks/log?${searchParams}`)
  }

  async getSpendAnalysis(params = {}) {
    const searchParams = new URLSearchParams()
    if (params.range) searchParams.append('range', params.range)
    if (params.startDate) searchParams.append('start_date', params.startDate)
    if (params.endDate) searchParams.append('end_date', params.endDate)
    return this.request(`/admin/clicks/spend-analysis?${searchParams}`)
  }

  async getTopCities(params = {}) {
    const searchParams = new URLSearchParams()
    if (params.range) searchParams.append('range', params.range)
    if (params.startDate) searchParams.append('startDate', params.startDate)
    if (params.endDate) searchParams.append('endDate', params.endDate)
    if (params.limit) searchParams.append('limit', params.limit)
    return this.request(`/admin/clicks/top-cities?${searchParams}`)
  }

  async getDevices(params = {}) {
    const searchParams = new URLSearchParams()
    if (params.range) searchParams.append('range', params.range)
    if (params.startDate) searchParams.append('startDate', params.startDate)
    if (params.endDate) searchParams.append('endDate', params.endDate)
    return this.request(`/admin/clicks/devices?${searchParams}`)
  }

  async getTargetingMatch(params = {}) {
    const searchParams = new URLSearchParams()
    if (params.range) searchParams.append('range', params.range)
    if (params.startDate) searchParams.append('startDate', params.startDate)
    if (params.endDate) searchParams.append('endDate', params.endDate)
    return this.request(`/admin/clicks/targeting-match?${searchParams}`)
  }

  // Proxies
  // Landing Pages
  async getLandingPages(params = {}) {
    const searchParams = new URLSearchParams()
    if (params.range) searchParams.append('range', params.range)
    if (params.startDate) searchParams.append('startDate', params.startDate)
    if (params.endDate) searchParams.append('endDate', params.endDate)
    return this.request(`/admin/clicks/landing-pages?${searchParams}`)
  }

  // Number Performance - calls grouped by phone number
  async getNumberPerformance(params = {}) {
    const searchParams = new URLSearchParams()
    if (params.range) searchParams.append('range', params.range)
    if (params.startDate) searchParams.append('startDate', params.startDate)
    if (params.endDate) searchParams.append('endDate', params.endDate)
    if (params.includeClicks !== undefined) searchParams.append('includeClicks', params.includeClicks)
    return this.request(`/admin/clicks/number-performance?${searchParams}`)
  }

  async getNumberPerformanceDaily(days = 14, perPhone = false) {
    const searchParams = new URLSearchParams()
    searchParams.append('days', days)
    if (perPhone) searchParams.append('perPhone', 'true')
    return this.request(`/admin/clicks/number-performance/daily?${searchParams}`)
  }

  async getCallvictionProbeNumbers() {
    return this.request('/admin/callviction-probe/numbers', { maxRetries: 1 })
  }

  // Landing Page Performance - landing pages with clicks/conversions/ROI
  async getLandingPageStats(params = {}) {
    const searchParams = new URLSearchParams()
    if (params.range) searchParams.append('range', params.range)
    if (params.startDate) searchParams.append('startDate', params.startDate)
    if (params.endDate) searchParams.append('endDate', params.endDate)
    return this.request(`/admin/clicks/landing-page-stats?${searchParams}`)
  }

  // Hourly Performance - click-to-call ratio by hour
  // First call without clicks (fast), second call with clicks (slow but cached)
  // datePreset: Binom preset (today, yesterday, last_7_days, etc.) for accurate API-based hourly data
  async getHourlyPerformance(params = {}) {
    const searchParams = new URLSearchParams()
    if (params.date) searchParams.append('date', params.date)
    if (params.datePreset) searchParams.append('datePreset', params.datePreset)
    if (params.includeClicks) searchParams.append('includeClicks', 'true')
    return this.request(`/admin/clicks/number-performance/hourly?${searchParams}`)
  }

  // Phone History - historical daily performance for a specific phone number
  async getPhoneHistory(phone, params = {}) {
    const searchParams = new URLSearchParams()
    if (params.range) searchParams.append('range', params.range)
    if (params.startDate) searchParams.append('startDate', params.startDate)
    if (params.endDate) searchParams.append('endDate', params.endDate)
    return this.request(`/admin/clicks/phone/${encodeURIComponent(phone)}/history?${searchParams}`)
  }

  // ==========================================
  // CREATIVE LIBRARY
  // ==========================================

  // ==========================================
  // CAMPAIGN TEMPLATES
  // ==========================================

  async getCampaignTemplate(id) {
    return this.request(`/admin/campaign-templates/${id}`)
  }

  // Sync headline statuses from ads (auto-detect approved/rejected based on effective_status)
  async syncHeadlineStatuses(ads) {
    if (!Array.isArray(ads) || ads.length === 0) return { updated: 0 }
    return { updated: 0, retired: true, adsSeen: ads.length }
  }

  // ==========================================
  // HEADLINE LIBRARY V2 - Performance, Lifecycle
  // ==========================================

  // Get hourly headline performance insights (RENAMED to avoid collision with clicks/hourly)
  async getHeadlineHourlyPerformance(market = null) {
    const params = market ? `?market=${market}` : ''
    return this.request(`/admin/headlines/performance/hourly${params}`)
  }

  // Get current time-based context and recommendations
  async getTimeContext(market = null) {
    const params = market ? `?market=${market}` : ''
    return this.request(`/admin/headlines/performance/time-context${params}`)
  }

  // Get pattern lifecycle data
  async getPatternLifecycle(state = 'all') {
    return { patterns: [], state, retired: true }
  }

  // ==========================================
  // CAMPAIGN DEPLOYMENT
  // ==========================================

  async getDeployPreview(templateId, accountIds) {
    const params = accountIds?.length > 0 ? `?account_ids=${accountIds.join(',')}` : ''
    return this.request(`/admin/deploy/${templateId}/preview${params}`)
  }

  async executeDeploy(templateId, data) {
    return this.request(`/admin/deploy/${templateId}`, {
      method: 'POST',
      body: JSON.stringify(data),
    })
  }

  // =====================
  // Facebook Ads - Rejected Ads Scanning
  // =====================

  /**
   * Scan ALL profiles for rejected/problematic ads
   * Iterates through all extension profiles with valid tokens
   * @returns {object} { success, profiles, accounts, rejected: [] }
   */
  async scanRejectedAdsAllProfiles() {
    return this.request(`/admin/facebook-ads/scan-rejected-all`)
  }

  /**
   * Live-scan one account through the owning extension and write the rejected
   * count/detail rows back before returning.
   * @param {object} params
   * @param {string} params.profileId
   * @param {string} params.accountId
   * @returns {object} { success, account_id, rejected: [], writeback }
   */
  async scanRejectedAdsForAccount({ profileId, accountId }) {
    return this.request(`/extension/rejected-ads/scan`, {
      method: 'POST',
      body: JSON.stringify({
        profile_id: profileId,
        account_id: accountId,
      }),
      maxRetries: 1,
      timeoutMs: 120_000,
    })
  }

  /**
   * Near-instant read of the rejected-ad heartbeat cache.
   * Populated every 5 min by the scheduler — covers server-token accounts.
   * @param {object} opts
   * @param {boolean} [opts.refresh=false] - Force a fresh scan (bypass cache)
   * @returns {object} { success, fromCache, count, rejected: [], scanned_at, bms_scanned, accounts_scanned }
   */
  async getCachedRejectedAds({ refresh = false } = {}) {
    const q = refresh ? '?refresh=true' : ''
    return this.request(`/admin/facebook-ads/rejected-ads-cached${q}`)
  }

  /**
   * Live per-number rolling-60-min counters from Binom /info/landing +
   * /info/campaign. Warmed server-side every 30s.
   *
   * @param {object} opts
   * @param {boolean} [opts.refresh=false] - Force a fresh Binom fetch (used on page load)
   * @returns {object} {
   *   success, fromCache, scanned_at, duration_ms,
   *   pulse: { clicks_last_hour, leads_last_hour, profit_last_hour },
   *   numbers: [{ phone, clicks_last_hour, leads_last_hour, profit_last_hour,
   *               seconds_since_last_lead, landings, group_names, ... }]
   * }
   */
  async getLiveNumbers({ refresh = false } = {}) {
    const q = refresh ? '?refresh=true' : ''
    return this.request(`/admin/binom/live-numbers${q}`)
  }

  /**
   * Fire-and-forget trigger for the delta clicklog sync. Used on Number-
   * Performance page mount so per-click rows stream in within ~90s of the
   * user opening the page. Server returns 202 with the previous run's
   * summary while the sync runs async. When BINOM_DELTA_ENABLED is not set
   * on the server, returns { triggered: false, feature_enabled: false }
   * without hitting Binom.
   */
  async triggerClicklogDelta() {
    return this.request(`/admin/binom/clicklog-delta-now`, { method: 'POST' })
  }

  /**
   * Delete rejected ads via server-side API tokens
   * @param {Array} ads - Array of { id, name, profile_id, account_id }
   * @returns {object} { success, deleted, errors, results }
   */
  async deleteRejectedAds(ads) {
    return this.request(`/admin/facebook-ads/delete-rejected`, {
      method: 'POST',
      body: JSON.stringify({ ads }),
      headers: { 'Content-Type': 'application/json' }
    })
  }

  async getDeleteJob(jobId) {
    return this.request(`/admin/facebook-ads/delete-jobs/${jobId}`)
  }

  async waitForDeleteJob(jobId, {
    onResult,
    intervalMs = 2000,
    timeoutMs = 30 * 60 * 1000,
  } = {}) {
    const terminalStatuses = new Set(['completed', 'failed', 'cancelled'])
    const startedAt = Date.now()
    let lastResultCount = 0
    let lastJob = null

    while (Date.now() - startedAt < timeoutMs) {
      const response = await this.getDeleteJob(jobId)
      const job = response?.job || response?.data?.job
      if (!job) throw new Error('Delete job not found')

      lastJob = job
      const progress = job.progress || {}
      const results = Array.isArray(progress.results) ? progress.results : []
      const freshResults = results.slice(lastResultCount)
      lastResultCount = results.length

      if (typeof onResult === 'function') {
        freshResults.forEach((result) => onResult(result, job))
      }

      if (terminalStatuses.has(job.status)) return job
      await new Promise(resolve => setTimeout(resolve, intervalMs))
    }

    return lastJob || { id: jobId, status: 'running', progress: {} }
  }

  // =====================
  // Facebook Ads - Profile-Based API (Direct FB API, no extension needed)
  // =====================

  /**
   * Get all ad accounts for a profile via FB API
   */
  async getProfileAccounts(profileId) {
    return this.request(`/admin/facebook-ads/profile/${profileId}/accounts`)
  }

  /**
   * Get campaigns for an account via FB API
   */
  async getProfileCampaigns(profileId, accountId, datePreset = 'today') {
    return this.request(`/admin/facebook-ads/profile/${profileId}/accounts/${accountId}/campaigns?date_preset=${datePreset}`)
  }

  /**
   * Get adsets for an account via FB API
   */
  async getProfileAdsets(profileId, accountId, datePreset = 'today') {
    return this.request(`/admin/facebook-ads/profile/${profileId}/accounts/${accountId}/adsets?date_preset=${datePreset}`)
  }

  /**
   * Get ads for an account via FB API
   */
  async getProfileAds(profileId, accountId, datePreset = 'today') {
    return this.request(`/admin/facebook-ads/profile/${profileId}/accounts/${accountId}/ads?date_preset=${datePreset}`)
  }

  /**
   * Get pages for an account via FB API
   */
  async getProfilePages(profileId, accountId) {
    return this.request(`/admin/facebook-ads/profile/${profileId}/accounts/${accountId}/pages`)
  }

  /**
   * Get pixels for an account via FB API
   */
  async getProfilePixels(profileId, accountId) {
    return this.request(`/admin/facebook-ads/profile/${profileId}/accounts/${accountId}/pixels`)
  }

  /**
   * COMBINED: Get pages, pixels, and images in ONE request with server caching
   * Much faster than 3 separate calls - reduces network round trips and caches for 5 minutes
   * @param {string} profileId - Profile ID
   * @param {string} accountId - Account ID
   * @param {object} options - Options object
   * @param {boolean} options.skipCache - Skip server cache (for manual refresh)
   */
  async getProfileModalData(profileId, accountId, options = {}) {
    const params = new URLSearchParams()
    if (options.skipCache) params.set('skipCache', 'true')
    const queryString = params.toString()
    return this.request(`/admin/facebook-ads/profile/${profileId}/accounts/${accountId}/modal-data${queryString ? '?' + queryString : ''}`)
  }

  /**
   * BATCH: Get campaigns, adsets, and ads in ONE request
   * Much faster than 3 separate calls
   */
  async getProfileAccountAll(profileId, accountId, datePreset = 'today') {
    return this.request(`/admin/facebook-ads/profile/${profileId}/accounts/${accountId}/all?date_preset=${datePreset}`)
  }

  /**
   * Get geo breakdown for all accounts
   * Uses geo_intelligence_layer (permanent, never empty)
   * Returns spend/clicks/adsets grouped by geo (FR, BE, UK, DE) per account
   * @param {string} datePreset - Date preset (today, yesterday, last_7d)
   * @param {boolean} skipBinom - Skip Binom API call for faster initial load (no clicks data)
   */
  async getGeoBreakdown(datePreset = 'today', skipBinom = false) {
    const params = new URLSearchParams({ datePreset })
    if (skipBinom) params.append('skipBinom', 'true')
    return this.request(`/admin/facebook-ads/geo-breakdown-v2?${params.toString()}`)
  }

  // ============================================================
  // BOOTSTRAP & SESSION STATE
  // ============================================================

  /**
   * Bootstrap endpoint - Get ALL initial data in one request
   * Returns: profiles, accounts, selection, auth, mainData, modalData
   * Reduces 6+ requests to 1
   */
  async getAdsManagerBootstrap() {
    return this.request('/bootstrap/adsmanager')
  }

  /**
   * Update user's session state (persists selection across devices)
   */
  async updateSessionState({ profileId, accountId, datePreset }) {
    return this.post('/session/update', { profileId, accountId, datePreset })
  }

  /**
   * Cache ad accounts for a profile (after fetching from extension/API)
   */
  async cacheProfileAccounts(profileId, accounts) {
    return this.post('/bootstrap/cache-accounts', { profileId, accounts })
  }

  // ============================================
  // IMAGE HASH TRACKING
  // ============================================

  /**
   * Check if an image hash is blocked or risky
   */
  async checkImageHash(hash) {
    return this.get(`/admin/image-hash/check/${hash}`)
  }

  /**
   * Check multiple image hashes at once
   */
  async checkImageHashes(hashes) {
    return this.post('/admin/image-hash/check-multiple', { hashes })
  }

  /**
   * Get all blocked image hashes
   */
  async getBlockedImages() {
    return this.get('/admin/image-hash/blocked')
  }

  /**
   * Get risky images (not blocked but low approval rate)
   */
  async getRiskyImages() {
    return this.get('/admin/image-hash/risky')
  }

  /**
   * Get all tracked image hashes
   */
  async getAllImageHashes(status = null, limit = 100) {
    const params = new URLSearchParams()
    if (status) params.append('status', status)
    params.append('limit', limit)
    return this.get(`/admin/image-hash/all?${params.toString()}`)
  }

  /**
   * Block an image hash
   */
  async blockImageHash(hash, reason, imageUrl = null) {
    return this.post('/admin/image-hash/block', {
      hash,
      reason,
      image_url: imageUrl
    })
  }

  /**
   * Unblock an image hash
   */
  async unblockImageHash(hash) {
    return this.post('/admin/image-hash/unblock', { hash })
  }

  /**
   * Track usage of an image hash when creating an ad
   */
  async trackImageHashUsage(data) {
    return this.post('/admin/image-hash/track', data)
  }

  /**
   * Sync image hashes from performance cache
   */
  async syncImageHashes() {
    return this.post('/admin/image-hash/sync-from-cache')
  }

  /**
   * Get image hash statistics
   */
  async getImageHashStats() {
    return this.get('/admin/image-hash/stats')
  }

  // ============================================================
  // LANDING PAGE LIBRARY
  // ============================================================

  /**
   * Get saved landing pages for an account
   */
  async getLandingPages(accountId, market = null) {
    const params = new URLSearchParams()
    if (market) params.append('market', market)
    const query = params.toString() ? `?${params.toString()}` : ''
    return this.get(`/admin/landing-pages/${accountId}${query}`)
  }

  /**
   * Get UTM parameters for an account (with global fallback)
   */
  async getUtmParams(accountId, limit = 20) {
    return this.get(`/admin/landing-pages/utm-params/${accountId}?limit=${limit}`)
  }

  /**
   * Save UTM parameters for an account
   */
  async saveUtmParams(data) {
    return this.post('/admin/landing-pages/utm-params', data)
  }

  /**
   * Delete a saved UTM parameter
   */
  async deleteUtmParams(id) {
    return this.delete(`/admin/landing-pages/utm-params/${id}`)
  }

  /**
   * Save a new landing page
   */
  async saveLandingPage(data) {
    return this.post('/admin/landing-pages', data)
  }

  /**
   * Update a landing page
   */
  async updateLandingPage(id, data) {
    return this.put(`/admin/landing-pages/${id}`, data)
  }

  /**
   * Delete a landing page
   */
  async deleteLandingPage(id) {
    return this.delete(`/admin/landing-pages/${id}`)
  }

  /**
   * Increment landing page usage count
   */
  async useLandingPage(id) {
    return this.post(`/admin/landing-pages/${id}/use`)
  }

  /**
   * Set landing page as default for account+market
   */
  async setDefaultLandingPage(id) {
    return this.post(`/admin/landing-pages/${id}/default`)
  }

  /**
   * Get recently used URLs for an account (from tracking table)
   */
  async getRecentUrls(accountId, limit = 10) {
    return this.get(`/admin/ad-metadata/account-urls?account_id=${accountId}&limit=${limit}`)
  }

}

export const api = new ApiService()
export default api
